import React, { useEffect, useState } from 'react';
import { KeyRound, Plus, Loader2, Copy, Check, RefreshCw, Ban, Trash2, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api, API_BASE } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { toast } from 'sonner';

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const ENDPOINTS = [
  { path: '/external/citysense/trackings?date=YYYY-MM-DD', desc: 'Capturas (trackings) del día: estación, marca, BU, fecha, hora, precios por producto, GPS.' },
  { path: '/external/citysense/our-prices', desc: 'Precios oficiales vigentes de nuestras estaciones (BU, nombre, marca, precios self/full).' },
  { path: '/external/citysense/competitor-prices?date=YYYY-MM-DD', desc: 'Últimos precios conocidos de la competencia (al cierre del día indicado, opcional).' },
];

function KeyRevealDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const copy = async () => {
    const ok = await copyToClipboard(data.api_key);
    if (ok) { setCopied(true); toast.success('Llave copiada'); setTimeout(() => setCopied(false), 2000); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="api-key-reveal-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[20px]">Llave API generada · {data.name}</DialogTitle></DialogHeader>
        <p className="text-[12.5px] text-[#b0483a] font-medium">Guarda esta llave ahora. Por seguridad no se volverá a mostrar.</p>
        <div className="flex items-center gap-2">
          <pre className="flex-1 break-all whitespace-pre-wrap rounded-lg bg-[#1c1c1c] text-[#f8f6f1] p-3 text-[12px]" data-testid="api-key-value">{data.api_key}</pre>
          <Button variant="outline" size="icon" onClick={copy} className="h-9 w-9 shrink-0" data-testid="api-key-copy-btn">
            {copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
          </Button>
        </div>
        <p className="text-[12px] text-[#5a5a5a]">El cliente debe enviarla en el header <code className="bg-[#f0ede6] px-1 rounded">X-API-Key</code> en cada petición.</p>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApiClients() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [showDocs, setShowDocs] = useState(true);

  const load = () => {
    api.get('/admin/api-clients').then(r => setRows(r.data)).catch(() => toast.error('Error cargando clientes API'));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      const r = await api.post('/admin/api-clients', { name: name.trim() });
      setOpen(false); setName(''); setReveal(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear'); }
    finally { setSaving(false); }
  };

  const revoke = async (r) => {
    if (!window.confirm(`¿Revocar la llave de "${r.name}"? Dejará de funcionar de inmediato.`)) return;
    try { await api.post(`/admin/api-clients/${r.id}/revoke`); toast('Llave revocada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al revocar'); }
  };

  const regenerate = async (r) => {
    if (!window.confirm(`¿Regenerar la llave de "${r.name}"? La llave anterior dejará de funcionar.`)) return;
    try { const res = await api.post(`/admin/api-clients/${r.id}/regenerate`); setReveal(res.data); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al regenerar'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar el cliente "${r.name}" definitivamente?`)) return;
    try { await api.delete(`/admin/api-clients/${r.id}`); toast('Cliente eliminado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="space-y-5" data-testid="admin-api-clients-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-[30px] leading-tight">API Externa</h1>
          <p className="text-[12.5px] text-[#5a5a5a] mt-1">Credenciales para partners (CitySense) · autenticación por header <code className="bg-[#f0ede6] px-1 rounded">X-API-Key</code>.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="api-client-new-btn">
          <Plus size={14} className="mr-1.5"/> Nuevo cliente API
        </Button>
      </header>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-[#fafaf6] text-left text-[#5a5a5a]">
              <th className="px-4 py-2.5 font-medium">Cliente</th>
              <th className="px-4 py-2.5 font-medium">Llave (ID)</th>
              <th className="px-4 py-2.5 font-medium">Estado</th>
              <th className="px-4 py-2.5 font-medium">Creada</th>
              <th className="px-4 py-2.5 font-medium">Último uso</th>
              <th className="px-4 py-2.5 font-medium text-right">Peticiones</th>
              <th className="px-4 py-2.5 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8a8579]">
                <KeyRound size={18} className="mx-auto mb-2"/>Sin clientes API. Crea uno para CitySense.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-[#f0ede6]" data-testid={`api-client-row-${r.key_id}`}>
                <td className="px-4 py-2.5 font-medium">{r.name}</td>
                <td className="px-4 py-2.5 font-mono text-[11.5px] text-[#5a5a5a]">pcx_{r.key_id}_•••</td>
                <td className="px-4 py-2.5">
                  {r.active
                    ? <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px]">Activa</span>
                    : <span className="inline-flex items-center rounded-full bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-[11px]">Revocada</span>}
                </td>
                <td className="px-4 py-2.5 text-[#5a5a5a]">{fmtDT(r.created_at)}</td>
                <td className="px-4 py-2.5 text-[#5a5a5a]">{fmtDT(r.last_used_at)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.request_count}</td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    {r.active && (
                      <>
                        <Button variant="outline" size="sm" className="h-8 text-[11.5px]" onClick={() => regenerate(r)} data-testid={`api-client-regen-${r.key_id}`}>
                          <RefreshCw size={12} className="mr-1"/> Regenerar
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-[11.5px] text-[#b0483a] border-[#e8c9c3] hover:bg-red-50" onClick={() => revoke(r)} data-testid={`api-client-revoke-${r.key_id}`}>
                          <Ban size={12} className="mr-1"/> Revocar
                        </Button>
                      </>
                    )}
                    {!r.active && (
                      <Button variant="outline" size="sm" className="h-8 text-[11.5px] text-[#b0483a]" onClick={() => remove(r)} data-testid={`api-client-delete-${r.key_id}`}>
                        <Trash2 size={12} className="mr-1"/> Eliminar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#e8e5de] rounded-xl p-4 space-y-3">
        <button onClick={() => setShowDocs(!showDocs)} className="flex items-center gap-2 text-[13px] font-medium" data-testid="api-docs-toggle">
          <BookOpen size={14}/> Documentación para el partner {showDocs ? '▾' : '▸'}
        </button>
        {showDocs && (
          <div className="space-y-3 text-[12.5px]">
            <p className="text-[#5a5a5a]">Base URL: <code className="bg-[#f0ede6] px-1.5 py-0.5 rounded break-all">{API_BASE}</code></p>
            <div className="space-y-2">
              {ENDPOINTS.map(e => (
                <div key={e.path} className="border border-[#f0ede6] rounded-lg p-3">
                  <code className="text-[12px] font-medium">GET {e.path}</code>
                  <p className="text-[#5a5a5a] mt-1">{e.desc}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="font-medium mb-1">Ejemplo (curl):</p>
              <pre className="bg-[#1c1c1c] text-[#f8f6f1] rounded-lg p-3 text-[11.5px] overflow-x-auto">{`curl "${API_BASE}/external/citysense/trackings?date=2026-06-15" \\
  -H "X-API-Key: pcx_xxxxxxxx_xxxxxxxxxxxx"`}</pre>
            </div>
            <p className="text-[#8a8579]">Fechas y horas en zona horaria America/Guatemala. Sin fecha, <code>trackings</code> devuelve el día actual.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="api-client-create-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[20px]">Nuevo cliente API</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. CitySense" className="h-10 text-[13px]" data-testid="api-client-name-input"/>
            <Button onClick={create} disabled={saving || !name.trim()} className="w-full h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1]" data-testid="api-client-create-btn">
              {saving ? <Loader2 size={14} className="animate-spin"/> : 'Generar credenciales'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <KeyRevealDialog data={reveal} onClose={() => setReveal(null)}/>
    </div>
  );
}
